import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Aspiration, AspirationFormData } from '../types/aspirations';
import { personaService } from '../services/personaService';
import { goalsService } from '../services/goalsService';
import { listsService } from '../services/listsService';

// ID generator for new items
const generateId = () => Math.random().toString(36).substr(2, 9);

interface AspirationsStore {
  aspirations: Aspiration[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  initializeAspirations: () => void;
  createAspiration: (data: AspirationFormData) => void;
  updateAspiration: (id: string, data: Partial<Aspiration>) => void;
  deleteAspiration: (id: string) => void;
  reorderAspirations: (reorderedAspirations: Aspiration[]) => void;
  getAspirationById: (id: string) => Aspiration | undefined;
  
  // UI State
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  
  // Helper
  saveToStorage: (aspirations: Aspiration[]) => void;
}

const useAspirationsStore = create<AspirationsStore>()(
  devtools(
    (set, get) => ({
      aspirations: [],
      isLoading: false,
      error: null,

      initializeAspirations: () => {
        console.log('🚀 Initializing aspirations store');
        try {
          // Ensure persona data is loaded
          personaService.initializeIfNeeded();
          
          // Load from localStorage
          const stored = localStorage.getItem('godmode_aspirations');
          if (stored) {
            const aspirations = JSON.parse(stored);
            console.log('📋 Loaded aspirations from localStorage:', aspirations.length);
            set({ aspirations });
          } else {
            console.log('📋 No aspirations found in localStorage');
            set({ aspirations: [] });
          }
        } catch (error) {
          console.error('Failed to initialize aspirations:', error);
          set({ error: 'Failed to load aspirations' });
        }
      },

      saveToStorage: (aspirations: Aspiration[]) => {
        try {
          localStorage.setItem('godmode_aspirations', JSON.stringify(aspirations));
        } catch (error) {
          console.error('Failed to save aspirations to localStorage:', error);
        }
      },

      createAspiration: (data: AspirationFormData) => {
        const state = get();
        const maxPriority = Math.max(...state.aspirations.map(a => a.priority), 0);
        
        const newAspiration: Aspiration = {
          id: generateId(),
          title: data.title,
          description: data.description,
          category: data.category,
          priority: data.priority || maxPriority + 1,
          status: 'not_started',
          progress: 0,
          deadline: data.deadline,
          createdAt: new Date(),
          updatedAt: new Date(),
          linkedGoals: []
        };

        console.log('➕ Creating new aspiration:', newAspiration.title);
        
        // If priority conflicts, shift others down
        const aspirationsToShift = state.aspirations.filter(a => a.priority >= newAspiration.priority);
        const shiftedAspirations = aspirationsToShift.map(a => ({
          ...a,
          priority: a.priority + 1,
          updatedAt: new Date()
        }));
        
        const otherAspirations = state.aspirations.filter(a => a.priority < newAspiration.priority);
        
        const newAspirations = [
          ...otherAspirations,
          newAspiration,
          ...shiftedAspirations
        ].sort((a, b) => a.priority - b.priority);
        
        get().saveToStorage(newAspirations);
        set({ aspirations: newAspirations });
      },

      updateAspiration: (id: string, data: Partial<Aspiration>) => {
        console.log('✏️ Updating aspiration:', id);
        const state = get();
        const newAspirations = state.aspirations.map(aspiration =>
          aspiration.id === id 
            ? { ...aspiration, ...data, updatedAt: new Date() }
            : aspiration
        );
        get().saveToStorage(newAspirations);
        set({ aspirations: newAspirations });
      },

      deleteAspiration: (id: string) => {
        console.log('🗑️ Deleting aspiration:', id);

        try {
          // Step 1: Get all goals linked to this aspiration
          const linkedGoals = goalsService.getGoalsByAspirationId(id);
          console.log(`📋 Found ${linkedGoals.length} goals to delete`);

          // Step 2: For each goal, delete its lists first
          linkedGoals.forEach(goal => {
            const linkedLists = listsService.getListsByGoalId(goal.id);
            console.log(`📝 Found ${linkedLists.length} lists to delete for goal "${goal.title}"`);

            // Delete each list (synchronously since deleteList is async but we need immediate effect)
            linkedLists.forEach(list => {
              listsService.deleteList(list.id);
              console.log(`🗑️ Deleted list: ${list.title}`);
            });

            // Delete the goal
            goalsService.deleteGoal(goal.id);
            console.log(`🗑️ Deleted goal: ${goal.title}`);
          });

          // Step 3: Delete the aspiration
          const state = get();
          const newAspirations = state.aspirations.filter(aspiration => aspiration.id !== id);
          get().saveToStorage(newAspirations);
          set({ aspirations: newAspirations });

          console.log('✅ Aspiration and all linked data deleted successfully');
        } catch (error) {
          console.error('Failed to delete aspiration:', error);
          throw error;
        }
      },

      reorderAspirations: (reorderedAspirations: Aspiration[]) => {
        console.log('🔄 Reordering aspirations');
        // Reassign priorities based on new order
        const aspirationsWithNewPriorities = reorderedAspirations.map((aspiration, index) => ({
          ...aspiration,
          priority: index + 1,
          updatedAt: new Date()
        }));
        
        get().saveToStorage(aspirationsWithNewPriorities);
        set({ aspirations: aspirationsWithNewPriorities });
      },

      getAspirationById: (id: string) => {
        const state = get();
        return state.aspirations.find(aspiration => aspiration.id === id);
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      setError: (error: string | null) => {
        set({ error });
      }
    }),
    {
      name: 'aspirations-store'
    }
  )
);

export { useAspirationsStore };